import axios from "axios";
import  { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUserAuth } from "../../context/UserAuthContext";
import { toast } from "react-toastify";

const BirthdayClientQuotationForm = () => {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;

  const { quotation_id } = useParams();
  const [singleQuotation, setSingleQuotation] = useState({});
  const [quotationItems, setQuotationItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const navigate = useNavigate();
  const { user, token } = useUserAuth();
  const merchant_id = user?.uid;
  const [currentClientData, setCurrentClientData] = useState({});
  const [discountType, setDiscountType] = useState("");
  const [discount, setDiscount] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);

  const fetchCurrentClientData = useCallback(async () => {
    if (!quotation_id) return;
    try {
      setIsFetching(true);
      const res = await axios.get(
        `${base_url}/api/v1/quotation/wedding-quotation/get-client_quotation`,
        {
          params: { merchant_id: merchant_id, quotation_id },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const clientData = res.data.data || {};
      setCurrentClientData(clientData);
      if (clientData.quotation_item && clientData.quotation_item.length > 0) {
        setQuotationItems(clientData.quotation_item);
      }
      if (clientData.notes) {
        setNotes(clientData.notes);
      }

      if (clientData.discount_type) {
        setDiscountType(clientData.discount_type);
      }

      if (clientData.discount_amount) {
        setDiscount(clientData.discount_amount);
      }

      if (clientData.total_discount) {
        setTotalDiscount(clientData.total_discount);
      }
    } catch (error) {
      console.error("Error fetching current client data:", error);
      toast.error("Failed to load existing quotation data");
    } finally {
      setIsFetching(false);
    }
  }, [quotation_id, base_url, merchant_id, token]);

  useEffect(() => {
    fetchCurrentClientData();
  }, [fetchCurrentClientData]);

  const getSelectedNames = useCallback(
    (arr = []) =>
      arr
        ?.filter((i) => i?.isSelected)
        ?.map((i) => i?.name)
        ?.join(", ") || "N/A",
    []
  );

  const getEventTypesWithPax = useCallback(
    (arr = []) =>
      arr
        ?.filter((i) => i?.isSelected)
        ?.map((i) => `${i.name}`)
        ?.join(", ") || "N/A",
    []
  );

  const formatEventDate = useCallback((event_date) => {
    if (!event_date) return "N/A";
    try {
      if (typeof event_date === "string") {
        const eventDateString = event_date.replace(/[{}"]/g, "");
        const timestamps = eventDateString.match(/\d+/g) || [];

        return timestamps
          .map((ts) => {
            const timestamp =
              ts.length === 10 ? parseInt(ts, 10) * 1000 : parseInt(ts, 10);
            return new Date(timestamp)
              .toLocaleDateString("en-GB")
              .replace(/\//g, "-");
          })
          .join(", ");
      }

      return new Date(event_date)
        .toLocaleDateString("en-GB")
        .replace(/\//g, "-");
    } catch (error) {
      console.error("Date formatting error:", error);
      return "Invalid Date";
    }
  }, []);

  const initializeQuotationItems = useCallback(() => {
    if (!singleQuotation || Object.keys(singleQuotation).length === 0) return;

    // Prevent re-initialization if already created
    if (quotationItems && quotationItems.length > 0) return;

    const initialItems = [];
    const now = Date.now();
    const roomCount = parseInt(singleQuotation.no_of_room) || 0;

    if (roomCount > 0) {
      initialItems.push({
        id: now + 1,
        category: "Room",
        label: `Rooms`,
        price: "",
        roomCount: roomCount,
        quantity: 1,
      });
    }

    if (singleQuotation?.is_theme_required) {
      initialItems.push({
        id: now + 100,
        category: "Decor",
        label: singleQuotation?.birthday_theme,
        price: "",
        quantity: 1,
        roomCount: 1,
      });
    }

    if (singleQuotation?.catering_required) {
      initialItems.push({
        id: now + 50,
        category: "Menu",
        label: singleQuotation?.menu_price || "",
        price: "",
        pax:
          singleQuotation?.event_options?.find((opt) => opt.isSelected)?.pax ||
          "",
        quantity: 1,
        roomCount: 1,
      });
    }

    const venueNames = getSelectedNames(singleQuotation?.venue_options);
    if (venueNames !== "N/A") {
      initialItems.push({
        id: now + 300,
        category: "Venue",
        label: venueNames,
        price: "",
        quantity: 1,
        roomCount: 1,
      });
    }

    const additional = singleQuotation?.entertainment_options?.filter(
      (s) => s?.isSelected
    );
    if (additional?.length) {
      additional.forEach((service, index) => {
        initialItems.push({
          id: now + 400 + index,
          category: "Additional",
          label: service.name,
          price: "",
          quantity: 1,
          roomCount: 1,
        });
      });
    }

    setQuotationItems(initialItems);
  }, [singleQuotation]);
  useEffect(() => {
    initializeQuotationItems();
  }, [initializeQuotationItems]);

  const handleChange = (id, field, value) => {
    setQuotationItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleRemoveItem = (id) => {
    setQuotationItems((prev) => {
      const filtered = prev.filter((item) => item.id !== id);
      return filtered.length > 0 ? filtered : prev;
    });
  };

  const calculateTotal = () =>
    quotationItems.reduce((sum, item) => {
      const price = Number(item.price) || 0;

      if (item.category === "Room" && item.roomCount) {
        return sum + price * item.roomCount;
      }
      if (item.category === "Menu" && item.pax) {
        return sum + price * item.pax;
      }
      const quantity = Number(item.quantity) || 1;
      return sum + price * quantity;
    }, 0);

  // --- Add New Rows ---
  const handleAddRoomRow = () => {
    const newItem = {
      id: Date.now(),
      category: "Room",
      label: "",
      price: "",
      roomCount: 1,
      quantity: 1,
    };
    setQuotationItems((prev) => [...prev, newItem]);
  };

  const handleAddMenuRow = () => {
    const newItem = {
      id: Date.now(),
      category: "Menu",
      label: "",
      pax: "",
      price: "",
      quantity: 1,
      roomCount: 1,
    };
    setQuotationItems((prev) => [...prev, newItem]);
  };

  const handleAddDecorRow = () => {
    const newItem = {
      id: Date.now(),
      category: "Decor",
      label: "",
      quantity: 1,
      price: "",
      roomCount: 1,
    };
    setQuotationItems((prev) => [...prev, newItem]);
  };

  const handleAddVenueRow = () => {
    const newItem = {
      id: Date.now(),
      category: "Venue",
      label: "",
      quantity: 1,
      price: "",
      roomCount: 1,
    };
    setQuotationItems((prev) => [...prev, newItem]);
  };

  const handleAddAdditionalRow = () => {
    const newItem = {
      id: Date.now(),
      category: "Additional",
      label: "",
      quantity: 1,
      price: "",
      roomCount: 1,
    };
    setQuotationItems((prev) => [...prev, newItem]);
  };

  // --- Helper Functions ---
  const getCategoryItems = (category) =>
    quotationItems.filter((item) => item.category === category);

  const getQuotationDetails = async () => {
    if (!quotation_id) return;
    try {
      setIsLoading(true);
      const res = await axios.get(
        `${base_url}/api/v1/quotation/birthday-quotation/${quotation_id}`
      );
      setSingleQuotation(res.data.data || {});
    } catch (error) {
      console.error("Error fetching quotation:", error);
      toast.error("Failed to load quotation details");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (quotation_id) getQuotationDetails();
  }, [quotation_id]);

  // Update discount calculation
  useEffect(() => {
    if (!discountType) {
      setTotalDiscount(0);
      return;
    }
    let res = 0;
    if (discountType === "percent") {
      res = Number((calculateTotal() * discount) / 100).toFixed(2);
    } else {
      res = Number(discount).toFixed(2);
    }
    setTotalDiscount(res);
  }, [discount, discountType, calculateTotal]);
  const maskMobile = (number) => {
    if (!number) return "N/A";
    const cleaned = number.replace(/\D/g, "");
    if (cleaned.length <= 5) return cleaned;
    return cleaned.slice(0, 5) + "*****";
  };
  const submitPdfDetails = async () => {
    if (!quotation_id || !merchant_id) {
      toast.error("Missing required information");
      return;
    }
    // Validate that all items have prices
    const itemsWithoutPrices = quotationItems.filter(
      (item) => !item.price || item.price === ""
    );
    if (itemsWithoutPrices.length > 0) {
      toast.error("Please add prices for all items before saving");
      return;
    }
    try {
      setIsLoading(true);
      const payload = {
        quotationItems: quotationItems.map((item) => ({
          ...item,
          price: Number(item.price) || 0,
          quantity: Number(item.quantity) || 1,
          roomCount: Number(item.roomCount) || 1,
          pax: item.pax ? Number(item.pax) : undefined,
        })),
        notes,
        quotation_id,
        merchant_id,
        discount_amount: discount,
        discount_type: discountType,
        total_discount: totalDiscount,
        quotation_type: "birthday",
        total_amount: calculateTotal(),
        ...(currentClientData.id && { id: currentClientData.id }),
      };

      const url = currentClientData.id
        ? `${base_url}/api/v1/quotation/wedding-quotation/update-client-quotation`
        : `${base_url}/api/v1/quotation/wedding-quotation/generate-client-quotation`;

      const method = currentClientData.id ? "put" : "post";

      const res = await axios[method](url, payload);

      toast.success(
        currentClientData.id
          ? "Quotation updated successfully!"
          : "Quotation PDF generated and saved successfully."
      );
      navigate("/dashboard/package-lead");
    } catch (error) {
      console.error("Error saving quotation:", error);
      toast.error(error.response?.data?.message || "Failed to save quotation");
    } finally {
      setIsLoading(false);
    }
  };

  const isEditing = !!currentClientData.id;

  if ((isLoading && Object.keys(singleQuotation).length === 0) || isFetching) {
    return (
      <div className="page-wrapper">
        <div className="container-fluid py-4">
          <div className="col-md-10 mx-auto">
            <div className="card shadow-lg border-0">
              <div className="card-body p-4 text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">
                  {isFetching
                    ? "Loading existing quotation..."
                    : "Loading quotation details..."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderRoomSection = () => {
    const roomItems = getCategoryItems("Room");
    return (
      <>
        <h6 className="fw-bold text-success d-flex justify-content-between align-items-center mb-3">
          <span>Rooms</span>
        </h6>

        {roomItems.map((item) => (
          <div
            key={item.id}
            className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2 gap-md-3 mb-3 p-3 border rounded"
          >
            <div className="flex-grow-1 mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Room Type
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="e.g., Luxury Room, Basic Room, Sweet Room etc."
                value={item.label}
                onChange={(e) => handleChange(item.id, "label", e.target.value)}
              />
            </div>
            <div className="flex-grow-1 mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Price per Room
              </label>
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                placeholder="Price per room"
                value={item.price}
                onChange={(e) => handleChange(item.id, "price", e.target.value)}
              />
            </div>
            <div className="flex-grow-1 mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Number of Rooms
              </label>
              <input
                type="number"
                min="1"
                className="form-control form-control-sm"
                placeholder="Number of rooms"
                value={item.roomCount || 1}
                onChange={(e) =>
                  handleChange(
                    item.id,
                    "roomCount",
                    Math.max(1, parseInt(e.target.value) || 1)
                  )
                }
              />
            </div>
            <div className="d-flex flex-column justify-content-center mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Subtotal
              </label>
              <div className="fw-bold text-primary fs-6">
                ₹
                {(
                  (Number(item.price) || 0) * (item.roomCount || 1)
                ).toLocaleString()}
              </div>
            </div>
            <div className="d-flex align-items-end">
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => handleRemoveItem(item.id)}
                disabled={roomItems.length <= 1}
              >
                <span className="d-none d-sm-inline">Remove</span>
                <span className="d-sm-none">Remove</span>
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="btn mb-2 btn-primary btn-sm  w-md-auto"
          onClick={handleAddRoomRow}
        >
          + Room Type
        </button>
      </>
    );
  };

  const renderDecorSection = () => {
    const decorItems = getCategoryItems("Decor");
    return (
      <>
        <h6 className="fw-bold text-success d-flex justify-content-between align-items-center mb-3">
          <span>Decor</span>
        </h6>

        {decorItems.map((item) => (
          <div
            key={item.id}
            className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2 gap-md-3 mb-3 p-3 border rounded"
          >
            <div className="flex-grow-1 mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Decor Type
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="e.g., Premium Decor, Basic Decor"
                value={item.label}
                onChange={(e) => handleChange(item.id, "label", e.target.value)}
              />
            </div>
            <div className="flex-grow-1 mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Quantity
              </label>
              <input
                type="number"
                min="1"
                className="form-control form-control-sm"
                placeholder="Quantity"
                value={item.quantity}
                onChange={(e) =>
                  handleChange(
                    item.id,
                    "quantity",
                    Math.max(1, parseInt(e.target.value) || 1)
                  )
                }
              />
            </div>
            <div className="flex-grow-1 mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Per Unit Price
              </label>
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                placeholder="Price"
                value={item.price}
                onChange={(e) => handleChange(item.id, "price", e.target.value)}
              />
            </div>
            <div className="d-flex flex-column justify-content-center mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Subtotal
              </label>
              <div className="fw-bold text-primary fs-6">
                ₹
                {(
                  (Number(item.price) || 0) * (item.quantity || 1)
                ).toLocaleString()}
              </div>
            </div>
            <div className="d-flex align-items-end">
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => handleRemoveItem(item.id)}
                disabled={decorItems.length <= 1}
              >
                <span className="d-none d-sm-inline">Remove</span>
                <span className="d-sm-none">Remove</span>
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="btn mb-2 btn-primary btn-sm w-md-auto"
          onClick={handleAddDecorRow}
        >
          + Decor Type
        </button>
      </>
    );
  };

  const renderMenuSection = () => {
    const menuItems = getCategoryItems("Menu");
    return (
      <>
        <h6 className="fw-bold text-success d-flex justify-content-between align-items-center mb-3">
          <span>Menu ({singleQuotation?.food_type})</span>
        </h6>

        {menuItems.map((item) => (
          <div
            key={item.id}
            className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2 gap-md-3 mb-3 p-3 border rounded"
          >
            <div className="flex-grow-1 mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Menu Type
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="e.g., Veg Menu, Veg & Non-Veg Menu etc."
                value={item.label}
                onChange={(e) => handleChange(item.id, "label", e.target.value)}
              />
            </div>
            <div className="flex-grow-1 mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">Pax</label>
              <input
                type="number"
                min="1"
                className="form-control form-control-sm"
                placeholder="Pax"
                value={item.pax}
                onChange={(e) =>
                  handleChange(
                    item.id,
                    "pax",
                    Math.max(1, parseInt(e.target.value) || 1)
                  )
                }
              />
            </div>
            <div className="flex-grow-1 mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Per Pax Price
              </label>
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                placeholder="Price"
                value={item.price}
                onChange={(e) => handleChange(item.id, "price", e.target.value)}
              />
            </div>
            <div className="d-flex flex-column justify-content-center mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Subtotal
              </label>
              <div className="fw-bold text-primary fs-6">
                ₹
                {((Number(item.price) || 0) * (item.pax || 1)).toLocaleString()}
              </div>
            </div>
            <div className="d-flex align-items-end">
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => handleRemoveItem(item.id)}
                disabled={menuItems.length <= 1}
              >
                <span className="d-none d-sm-inline">Remove</span>
                <span className="d-sm-none">Remove</span>
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="btn mb-2 btn-primary btn-sm w-md-auto"
          onClick={handleAddMenuRow}
        >
          + Menu Type
        </button>
      </>
    );
  };

  const renderVenueSection = () => {
    const venueItems = getCategoryItems("Venue");
    return (
      <>
        <h6 className="fw-bold text-success d-flex justify-content-between align-items-center mb-3">
          <span>Venue</span>
        </h6>

        {venueItems.map((item) => (
          <div
            key={item.id}
            className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2 gap-md-3 mb-3 p-3 border rounded"
          >
            <div className="flex-grow-1 mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Venue Type
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="e.g., Lawn, Venue, Banquet Hall, Poolside, etc."
                value={item.label}
                onChange={(e) => handleChange(item.id, "label", e.target.value)}
              />
            </div>
            <div className="flex-grow-1 mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Quantity
              </label>
              <input
                type="number"
                min="1"
                className="form-control form-control-sm"
                placeholder="Quantity"
                value={item.quantity}
                onChange={(e) =>
                  handleChange(
                    item.id,
                    "quantity",
                    Math.max(1, parseInt(e.target.value) || 1)
                  )
                }
              />
            </div>
            <div className="flex-grow-1 mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Per Unit Price
              </label>
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                placeholder="Price"
                value={item.price}
                onChange={(e) => handleChange(item.id, "price", e.target.value)}
              />
            </div>
            <div className="d-flex flex-column justify-content-center mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Subtotal
              </label>
              <div className="fw-bold text-primary fs-6">
                ₹
                {(
                  (Number(item.price) || 0) * (item.quantity || 1)
                ).toLocaleString()}
              </div>
            </div>
            <div className="d-flex align-items-end">
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => handleRemoveItem(item.id)}
                disabled={venueItems.length <= 1}
              >
                <span className="d-none d-sm-inline">Remove</span>
                <span className="d-sm-none">Remove</span>
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="btn mb-2 btn-primary btn-sm w-md-auto"
          onClick={handleAddVenueRow}
        >
          + Venue Type
        </button>
      </>
    );
  };

  const renderAdditionalSection = () => {
    const additionalItems = getCategoryItems("Additional");
    return (
      <>
        <h6 className="fw-bold text-success d-flex justify-content-between align-items-center mb-3">
          <span>Additional Requirements</span>
        </h6>

        {additionalItems.map((item) => (
          <div
            key={item.id}
            className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2 gap-md-3 mb-3 p-3 border rounded"
          >
            <div className="flex-grow-1 mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Service Name
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Additional service name"
                value={item.label}
                onChange={(e) => handleChange(item.id, "label", e.target.value)}
              />
            </div>
            <div className="flex-grow-1 mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Quantity
              </label>
              <input
                type="number"
                min="1"
                className="form-control form-control-sm"
                placeholder="Quantity"
                value={item.quantity}
                onChange={(e) =>
                  handleChange(
                    item.id,
                    "quantity",
                    Math.max(1, parseInt(e.target.value) || 1)
                  )
                }
              />
            </div>
            <div className="flex-grow-1 mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Per Unit Price
              </label>
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                placeholder="Price"
                value={item.price}
                onChange={(e) => handleChange(item.id, "price", e.target.value)}
              />
            </div>
            <div className="d-flex flex-column justify-content-center mb-2 mb-md-0">
              <label className="form-label small text-muted mb-1">
                Subtotal
              </label>
              <div className="fw-bold text-primary fs-6">
                ₹
                {(
                  (Number(item.price) || 0) * (item.quantity || 1)
                ).toLocaleString()}
              </div>
            </div>
            <div className="d-flex align-items-end">
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => handleRemoveItem(item.id)}
              >
                <span className="d-none d-sm-inline">Remove</span>
                <span className="d-sm-none">Remove</span>
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-primary btn-sm w-md-auto"
          onClick={handleAddAdditionalRow}
        >
          + Additional Service
        </button>
      </>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="container-fluid py-4">
        <div className="col-12 col-md-10 mx-auto">
          <div className="card shadow-lg border-0">
            <div className="card-body p-3 p-md-4">
              <h3 className="mb-4 text-center fw-bold text-primary fs-4 fs-md-3">
                {isEditing
                  ? "Update Birthday Quotation"
                  : "Birthday Quotation Details"}
              </h3>

              {/* Client Details */}
              <h5 className="fw-bold text-primary mb-3">Client Details</h5>
              <div className="table-responsive">
                <table className="table table-bordered align-middle mb-4">
                  <tbody>
                    <tr className="table-light">
                      <th className="w-25 ">
                        <b>Contact Person</b>
                      </th>
                      <td className="w-25">{singleQuotation?.name || "N/A"}</td>
                      <th className="w-25">
                        <b>Mobile Number</b>
                      </th>
                      <td className="w-25">
                        {maskMobile(singleQuotation?.mob_no) || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <b>Birthday Year</b>
                      </th>
                      <td>{singleQuotation?.birthday_year || "N/A"}</td>
                      <th>
                        <b>Location</b>
                      </th>
                      <td>
                        {singleQuotation?.preferred_location?.label || "N/A"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Event Details */}
              <h5 className="fw-bold text-primary mb-3">Event Details</h5>
              <div className="table-responsive">
                <table className="table table-bordered align-middle">
                  <tbody>
                    <tr>
                      <th className="w-25">
                        <b>Entertainment Options</b>
                      </th>
                      <td className="w-75">
                        {getEventTypesWithPax(
                          singleQuotation?.entertainment_options
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <b>Event Date</b>
                      </th>
                      <td>{singleQuotation?.event_date}</td>
                    </tr>
                    <tr>
                      <th>
                        <b>Food Type</b>
                      </th>
                      <td>{singleQuotation?.food_type || "N/A"}</td>
                    </tr>
                    {singleQuotation?.catering_required && (
                      <tr>
                        <th>
                          <b>Menu Price</b>
                        </th>
                        <td>{singleQuotation?.menu_price || "N/A"}</td>
                      </tr>
                    )}
                    <tr>
                      <th>
                        <b>Number Of Guests</b>
                      </th>
                      <td>{singleQuotation?.no_of_guest}</td>
                    </tr>
                    {singleQuotation?.venue_required && (
                      <>
                        <tr>
                          <th>
                            <b>Number Of Rooms</b>
                          </th>
                          <td>{singleQuotation?.no_of_room}</td>
                        </tr>
                      </>
                    )}

                    <tr>
                      <th>
                        <b>Venue Type</b>
                      </th>
                      <td>
                        {getSelectedNames(singleQuotation?.venue_options)}
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <b>Event Budget</b>
                      </th>
                      <td>{singleQuotation?.event_budget || "N/A"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Create/Update Client Quotation */}
              <h5 className="fw-bold text-primary mb-3 mt-4">
                {isEditing
                  ? "Update Client Quotation"
                  : "Create Client Quotation"}
              </h5>

              {/* Render all sections */}
              {renderRoomSection()}
              {singleQuotation?.is_theme_required && renderDecorSection()}
              {singleQuotation?.catering_required && renderMenuSection()}
              {renderVenueSection()}
              {renderAdditionalSection()}

              <div className="my-4">
                <label className="form-label">
                  Additional information (optional)
                </label>
                <textarea
                  className="form-control"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={5}
                  placeholder="Additional information"
                />
              </div>

              <div className="table-responsive">
                <table className="table table-bordered align-middle mb-4 mt-3">
                  <tbody>
                    <tr className="">
                      <td className="">
                        <select
                          onChange={(e) => setDiscountType(e.target.value)}
                          className="form-select"
                          value={discountType}
                        >
                          <option disabled value="">
                            Discount (If Any)
                          </option>
                          <option value="percent">Percent(%)</option>
                          <option value="amount">Fix Amount</option>
                        </select>
                      </td>
                      <td className="">
                        <input
                          type="number"
                          style={{ width: "170px" }}
                          disabled={!discountType}
                          min={1}
                          max={discountType === "percent" ? 100 : undefined}
                          value={discount === 0 ? "" : discount}
                          onChange={(e) => setDiscount(e.target.value)}
                        />
                        &nbsp; {discountType === "percent" ? "%" : <>&#8377;</>}
                      </td>
                      <td className="">{totalDiscount}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="fw-bold fs-5 text-end mt-4 p-3 bg-light rounded">
                Grand Total: ₹
                {totalDiscount
                  ? (
                      Number(calculateTotal()) - Number(totalDiscount)
                    ).toLocaleString()
                  : calculateTotal().toLocaleString()}
              </div>
              <div className="text-center mt-4">
                <button
                  className="btn btn-primary px-4 w-md-auto"
                  onClick={submitPdfDetails}
                  disabled={isLoading || quotationItems.length === 0}
                >
                  {isLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      {isEditing ? "Updating..." : "Generating..."}
                    </>
                  ) : isEditing ? (
                    "Update Final Quotation PDF"
                  ) : (
                    "Generate Final Quotation PDF"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BirthdayClientQuotationForm;
